/**
 * Rechnungen — die Freigabe-Warteschlange des Auftraggebers (spec §1.10, §1.12).
 *
 * Vier Entscheidungen, vier Dialoge, und jede sagt vorher, was sie tut: Freigeben zieht eine Nummer aus
 * der Serie des Ausstellers und ist nur noch durch eine Stornorechnung aufzuheben; Ablehnen braucht
 * einen Kommentar, den der Auftragnehmer per E-Mail liest; die Auszahlung wiederholt den Bruttobetrag,
 * weil eine Rechnung in genau einer Überweisung bezahlt wird; Storno erklärt, dass ein zweites,
 * negatives Dokument entsteht.
 *
 * Die Liste kommt aus `GET /api/t/:id/rechnungen?status=…`: Aussteller, Status, Nummer, Netto,
 * Umsatzsteuer, Brutto, `self_approved` und die Fahne `rueckbuchung` stehen dort. Steuerprofil,
 * Nummernserie und Kreditor stehen nicht daran — die holt `GET /api/t/:id/team` daneben.
 * `/provisionen?status=…` liefert die Positionen darunter. Der Bildschirm summiert nichts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo, useState, type ReactNode } from 'react'
import { cancelSchema, payoutSchema, rejectSchema } from '../../../../shared/schemas/billing'
import { api, type ProvisionZeile, type RechnungListenZeile, type TeamZeile } from '../../../api'
import { Betrag } from '../../../components/Betrag'
import { Dialog } from '../../../components/Dialog'
import { Feld } from '../../../components/Feld'
import { Knopf } from '../../../components/Knopf'
import { Leiste } from '../../../components/Leiste'
import { Tabelle } from '../../../components/Tabelle'
import { TabellenZustand, Zustand, fehlermeldung } from '../../../components/Zustand'
import { datum } from '../../../format'
import {
  STEUERPROFIL_TEXT, gruppiereRechnungen, serienName,
  type RechnungsGruppe, type RechnungsZeile,
} from '../../../rechnungenModell'
import type { SeitenProps } from '../../../screen'
import { berlinYear } from '../../../../shared/dates'

/**
 * Die beiden Reiter dieses Bildschirms und der Status, den Server und Provisionsliste dafür kennen.
 * Beide Abfragen nehmen denselben Wert: eine Rechnung ist `submitted`, solange ihre Positionen es
 * sind, und `approved`, sobald sie es sind (services/billing.ts).
 */
const STATUS_FILTER = { eingereicht: 'submitted', freigegeben: 'approved' } as const
export type RechnungenStatus = keyof typeof STATUS_FILTER
export interface RechnungenSuche { status: RechnungenStatus }

function istStatus(wert: unknown): wert is RechnungenStatus {
  return wert === 'eingereicht' || wert === 'freigegeben'
}

/** So viele <th> stehen im Kopf der Positionstabelle — die Zustandszeile spannt genau darüber. */
const SPALTEN = 8

/**
 * Der Status einer Rechnung auf Deutsch. Er steht neben der Nummer, sobald entschieden wurde: eine
 * stornierte und eine freigegebene Rechnung tragen dieselbe Nummer und denselben Bruttobetrag, und
 * ohne dieses Wort wäre der Unterschied zwischen „bezahlen" und „nicht bezahlen" nirgends zu sehen.
 */
const STATUS_TEXT: Readonly<Record<RechnungsZeile['status'], string>> = {
  draft: 'Entwurf',
  submitted: 'eingereicht',
  rejected: 'abgelehnt',
  approved: 'freigegeben',
  paid: 'ausgezahlt',
  cancelled: 'storniert',
}

/** Nur eine freigegebene oder ausgezahlte Rechnung hat ein Ausstellungs- und ein Fälligkeitsdatum,
 *  das noch etwas bedeutet. An einer abgelehnten oder stornierten wäre „zahlbar bis" eine Zusage. */
function istZahlbar(status: RechnungsZeile['status']): boolean {
  return status === 'approved' || status === 'paid'
}

/**
 * Die Felder, die Auszahlung und Storno brauchen — dieselben Werte, die der Server geliefert hat,
 * nur in der Form, die die beiden Dialoge und die Antworten von approve/reject tragen.
 */
function alsRechnungsZeile(gruppe: RechnungsGruppe): RechnungsZeile {
  return {
    id: gruppe.invoice_id,
    status: gruppe.status,
    number: gruppe.nummer,
    issued_at: gruppe.issued_at,
    due_at: gruppe.due_at,
    net_cents: gruppe.net_cents,
    vat_cents: gruppe.vat_cents,
    gross_cents: gruppe.gross_cents,
    self_approved: gruppe.selbstfreigabe ? 1 : 0,
  }
}

/**
 * Die erste zod-Meldung, wörtlich — dieselbe deutsche Karte, die der Server benutzt —, zusammen mit
 * dem Feld, das sie meint. Alle drei Dialoge hängen sie damit an dasselbe Eingabefeld, das der Server
 * beanstanden würde: nur dort trägt sie `aria-invalid` und `aria-describedby`.
 */
interface Eingabefehler {
  /** der Feldname aus dem Schema; `null`, wenn die Meldung dem ganzen Vorgang gilt */
  feld: string | null
  meldung: string
}

interface ZodMeldung { readonly path: readonly PropertyKey[]; readonly message: string }

function ersterFehler(fehler: { issues: readonly ZodMeldung[] }): Eingabefehler {
  const erste = fehler.issues[0]
  if (erste === undefined) return { feld: null, meldung: 'Die Eingabe ist ungültig.' }
  const feld = erste.path[0]
  return { feld: typeof feld === 'string' ? feld : null, meldung: erste.message }
}

/** Die Meldung für genau dieses Feld — oder `undefined`, damit `Feld` es nicht als ungültig markiert. */
function meldungFuer(fehler: Eingabefehler | null, feld: string): string | undefined {
  return fehler !== null && fehler.feld === feld ? fehler.meldung : undefined
}

export const Route = createFileRoute('/t/$tenantId/rechnungen')({
  validateSearch: (roh: Record<string, unknown>): RechnungenSuche => ({
    status: istStatus(roh.status) ? roh.status : 'eingereicht',
  }),
  component: RechnungenRoute,
})

function RechnungenRoute(): ReactNode {
  const { tenantId } = Route.useParams()
  const suche = Route.useSearch()
  const navigate = useNavigate()
  // `search` bekommt das fertige Objekt statt der Reduzierfunktion des Routers: deren `current` ist
  // die Vereinigung aller Suchparameter aller Routen, und dort ist `status` ein optionaler String —
  // hier ist der Reiter Pflicht, weil `validateSearch` ihn immer setzt.
  return (
    <RechnungenSeite
      tenantId={tenantId}
      suche={suche}
      setzeSuche={(naechste) => { void navigate({ to: '.', search: { ...suche, ...naechste } }) }}
      gehZu={(pfad) => { void navigate({ to: pfad }) }}
    />
  )
}

/** Welcher Dialog gerade offen ist. Immer nur einer: zwei Entscheidungen zugleich gibt es nicht. */
type OffenerDialog =
  | { art: 'freigabe' | 'ablehnung'; gruppe: RechnungsGruppe }
  | { art: 'auszahlung' | 'storno'; rechnung: RechnungsZeile }

/**
 * Die zuletzt getroffene Entscheidung, so wie der Server sie zurückgegeben hat. `storno` trägt die
 * zweite, negative Rechnung: der Storno-Dialog verspricht eine eigene Nummer aus derselben Serie,
 * und die ist erst eingelöst, wenn sie danach auch dasteht.
 */
interface Entscheidung {
  rechnung: RechnungsZeile
  storno: RechnungsZeile | null
}

export function RechnungenSeite({ tenantId, suche, setzeSuche }: SeitenProps<RechnungenSuche>): ReactNode {
  const qc = useQueryClient()
  const [offen, setOffen] = useState<OffenerDialog | null>(null)
  const [entschieden, setEntschieden] = useState<Entscheidung | null>(null)
  // Der Kalender des Browsers ist nicht die Uhr des Systems: der Server zieht die Nummer aus der
  // Serie des Berliner Geschäftsjahres (services/billing.ts, `number_year`). Am 31. Dezember um
  // 23:30 Uhr in Berlin steht in Los Angeles noch der 31. Dezember, in Auckland schon der 1. Januar
  // — und der Dialog verspräche dort eine Serie, aus der die Freigabe gar nicht zieht.
  const jahr = berlinYear(new Date())
  const serverStatus = STATUS_FILTER[suche.status]

  const rechnungen = useQuery({
    queryKey: ['rechnungen', tenantId, serverStatus],
    queryFn: () => api.get<{ rechnungen: RechnungListenZeile[] }>(
      `/api/t/${tenantId}/rechnungen`, { status: serverStatus }),
  })
  const provisionen = useQuery({
    queryKey: ['provisionen', tenantId, serverStatus],
    queryFn: () => api.get<{ provisionen: ProvisionZeile[] }>(
      `/api/t/${tenantId}/provisionen`, { status: serverStatus }),
  })
  // Steuerprofil, Nummernserie und Kreditor stehen nicht an der Rechnung (TenantInvoiceListRow),
  // sondern am Team-Eintrag des Ausstellers. Sie werden gebraucht, bevor freigegeben wird.
  const team = useQuery({
    queryKey: ['team', tenantId],
    queryFn: () => api.get<{ team: TeamZeile[] }>(`/api/t/${tenantId}/team`),
  })

  // Die Warteschlange fasst bis zu 5 000 Zeilen; jede Gruppe sortiert ihre Positionen. Das gehört
  // nicht in jeden Tastendruck eines Dialogs, sondern an die drei Antworten, aus denen es entsteht.
  const gruppen = useMemo(() => gruppiereRechnungen({
    rechnungen: rechnungen.data?.rechnungen ?? [],
    zeilen: provisionen.data?.provisionen ?? [],
    team: team.data?.team ?? [],
  }), [rechnungen.data, provisionen.data, team.data])

  /** Nach jeder Entscheidung wird verworfen statt nachgerechnet (Vertrag Teil 4). */
  const neuLaden = (): void => {
    for (const schl of ['rechnungen', 'provisionen', 'dashboard']) {
      void qc.invalidateQueries({ queryKey: [schl, tenantId] })
    }
  }

  const erledigt = (rechnung: RechnungsZeile, storno: RechnungsZeile | null = null): void => {
    setEntschieden({ rechnung, storno })
    setOffen(null)
    neuLaden()
  }

  return (
    <div className="seite">
      <h1>Rechnungen</h1>

      <Leiste beschriftung="Rechnungen filtern">
        <Feld label="Status" htmlFor="rg-status">
          <select
            id="rg-status"
            value={suche.status}
            onChange={(e) => { if (istStatus(e.target.value)) setzeSuche({ status: e.target.value }) }}
          >
            <option value="eingereicht">Eingereicht — zur Freigabe</option>
            <option value="freigegeben">Freigegeben — zur Auszahlung</option>
          </select>
        </Feld>
      </Leiste>

      {entschieden === null ? null : (
        <section className="detail" aria-label="Zuletzt entschieden">
          {/* Der Status steht in der Überschrift, weil Nummer und Bruttobetrag darunter für eine
              freigegebene und eine stornierte Rechnung dieselben sind. Ausstellung und Fälligkeit
              stehen nur an einer Rechnung, die noch zu bezahlen ist — an einer abgelehnten oder
              stornierten wären sie eine Zusage, die niemand mehr einlöst. */}
          <h2>
            Rechnung {entschieden.rechnung.number ?? entschieden.rechnung.id} —{' '}
            {STATUS_TEXT[entschieden.rechnung.status]}
          </h2>
          <p>
            Brutto <Betrag cents={entschieden.rechnung.gross_cents} />
            {!istZahlbar(entschieden.rechnung.status) || entschieden.rechnung.issued_at === null
              ? null
              : ` · ausgestellt am ${datum(entschieden.rechnung.issued_at)}`}
            {!istZahlbar(entschieden.rechnung.status) || entschieden.rechnung.due_at === null
              ? null
              : ` · zahlbar bis ${datum(entschieden.rechnung.due_at)}`}
          </p>
          {entschieden.storno === null ? null : (
            <p className="notiz">
              Stornorechnung {entschieden.storno.number ?? entschieden.storno.id} ausgestellt.
            </p>
          )}
          {/* Nur eine Freigabe kann eine Selbstfreigabe sein: `self_approved` wird beim Freigeben
              gesetzt und bei einer Ablehnung nie angefasst. */}
          {istZahlbar(entschieden.rechnung.status) ? (
            <p className="notiz">
              {entschieden.rechnung.self_approved === 1 ? 'Selbstfreigabe: ja' : 'Selbstfreigabe: nein'}
            </p>
          ) : null}
          {entschieden.rechnung.status === 'approved' ? (
            <p className="aktionen">
              <Knopf onClick={() => { setOffen({ art: 'auszahlung', rechnung: entschieden.rechnung }) }}>
                Auszahlung erfassen
              </Knopf>
              <Knopf
                variante="still"
                onClick={() => { setOffen({ art: 'storno', rechnung: entschieden.rechnung }) }}
              >
                Rechnung stornieren
              </Knopf>
            </p>
          ) : null}
        </section>
      )}

      {rechnungen.isPending ? <Zustand art="laedt" beschriftung="Die Rechnungen werden geladen." /> : null}
      {rechnungen.isError ? (
        <Zustand art="fehler" fehler={rechnungen.error} onWiederholen={() => { void rechnungen.refetch() }} />
      ) : null}
      {!rechnungen.isPending && !rechnungen.isError && gruppen.length === 0 ? (
        <Zustand
          art="leer"
          titel="Keine Rechnungen zur Freigabe"
          hinweis="Hier stehen die Abrechnungen Ihrer Auftragnehmer, sobald sie eingereicht sind."
        />
      ) : null}

      {gruppen.map((gruppe) => (
        // Ein benannter Bereich je Rechnung: „Freigeben" heißt in jeder Gruppe gleich, und erst der
        // Bereichsname sagt einer Vorlesesoftware, über wessen Abrechnung der Knopf entscheidet.
        <section
          key={gruppe.invoice_id}
          className="detail"
          aria-label={
            gruppe.art === 'cancellation'
              ? `Stornorechnung an ${gruppe.empfaenger_name}`
              : `Abrechnung von ${gruppe.empfaenger_name}`
          }
        >
          <h2>
            {gruppe.empfaenger_name}
            {gruppe.art === 'cancellation' ? ' — Stornorechnung' : ''}
          </h2>
          {/* Eine Stornorechnung wird mit `status='approved'` angelegt und steht deshalb in
              demselben Reiter wie die Rechnungen, die noch zu überweisen sind. Sie ist aber das
              negative Gegenstück einer bereits aufgehobenen Rechnung — wer sie „auszahlt", überweist
              einen negativen Betrag, und wer sie „storniert", storniert ein Storno. Beide Knöpfe
              fehlen hier deshalb, und der Satz sagt, warum. */}
          {gruppe.art === 'cancellation' ? (
            <p className="notiz">
              Diese Rechnung hebt eine frühere auf und wird nicht ausgezahlt. Sie steht hier, weil sie
              zur Nummernserie gehört und in der Buchhaltung mitläuft.
            </p>
          ) : null}
          <dl className="kennzahlen">
            <div>
              <dt>Steuerprofil des Ausstellers</dt>
              <dd className="leise">
                {gruppe.steuerprofil === null ? 'unbekannt' : STEUERPROFIL_TEXT[gruppe.steuerprofil]}
                {gruppe.profil_geprueft ? '' : ' — noch nicht geprüft'}
              </dd>
            </div>
            <div>
              <dt>Nummernserie</dt>
              <dd className="leise">{serienName(gruppe.rechnungspraefix, jahr)}</dd>
            </div>
            <div>
              <dt>Kreditor</dt>
              <dd className="leise">
                {gruppe.kreditor_no ?? 'wird bei der ersten Freigabe vergeben'}
              </dd>
            </div>
          </dl>

          {gruppe.rueckbuchung ? (
            <p className="fehler">
              Rechnung enthält Rückbuchung — hinter mindestens einer Position wartet die Stornierung
              einer bereits abgerechneten Zeile. Bitte vor der Freigabe prüfen.
            </p>
          ) : null}

          <Tabelle
            beschriftung={`Positionen der Abrechnung von ${gruppe.empfaenger_name}`}
            kopf={
              <tr>
                <th scope="col">#</th>
                <th scope="col">Leistung</th>
                <th scope="col">Rolle</th>
                <th scope="col">Kunde</th>
                <th scope="col">Produkt</th>
                <th scope="col" className="zahl">Rate</th>
                <th scope="col">Zahlungseingang</th>
                <th scope="col" className="zahl">Betrag</th>
              </tr>
            }
            fuss={
              /* Die drei Zahlen stammen unveraendert aus GET /api/t/:id/rechnungen. Eine Summe
                 ueber die Zeilen darueber waere eine zweite Rechnung und koennte abweichen,
                 sobald eine Position nach dem Einreichen zurueckgebucht wurde. */
              <>
                <tr>
                  <td colSpan={SPALTEN - 1}>Netto laut Rechnung</td>
                  <td className="zahl"><Betrag cents={gruppe.net_cents} /></td>
                </tr>
                <tr>
                  <td colSpan={SPALTEN - 1}>Umsatzsteuer</td>
                  <td className="zahl"><Betrag cents={gruppe.vat_cents} /></td>
                </tr>
                <tr>
                  <td colSpan={SPALTEN - 1}>Brutto laut Rechnung</td>
                  <td className="zahl"><Betrag cents={gruppe.gross_cents} /></td>
                </tr>
              </>
            }
          >
            {/* Die Positionen kommen aus einer eigenen Abfrage. Bleibt sie aus oder scheitert sie,
                sagt der Bildschirm genau das — eine leere Tabelle mit einer Erklärung, warum sie
                leer sei, wäre an dieser Stelle eine Behauptung über Daten, die er nicht hat. */}
            {provisionen.isPending ? (
              <TabellenZustand art="laedt" spalten={SPALTEN} beschriftung="Die Positionen werden geladen." />
            ) : provisionen.isError ? (
              <TabellenZustand
                art="fehler"
                spalten={SPALTEN}
                fehler={provisionen.error}
                onWiederholen={() => { void provisionen.refetch() }}
              />
            ) : gruppe.positionen.length === 0 ? (
              <TabellenZustand
                art="leer"
                spalten={SPALTEN}
                titel="Keine Positionen zu dieser Rechnung"
                hinweis="Die Provisionsliste hat zu diesem Filter keine Position dieser Rechnung geliefert. Die Beträge im Fuß stammen von der Rechnung selbst und gelten unabhängig davon."
              />
            ) : null}
            {gruppe.positionen.map((position) => (
              <tr key={position.id}>
                <th scope="row" className="mono">{position.ref_no}</th>
                <td>{position.leistung_text}</td>
                <td>{position.role_label}</td>
                <td>{position.customer_name ?? '—'}</td>
                <td>{position.product_code ?? '—'}</td>
                <td className="zahl">{position.rate_nr ?? '—'}</td>
                <td>{position.paid_at === null ? '—' : datum(position.paid_at)}</td>
                <td className="zahl"><Betrag cents={position.amount_cents} /></td>
              </tr>
            ))}
          </Tabelle>

          {gruppe.art === 'cancellation' ? null : suche.status === 'eingereicht' ? (
            <p className="aktionen">
              <Knopf onClick={() => { setOffen({ art: 'freigabe', gruppe }) }}>Freigeben</Knopf>
              <Knopf variante="warnung" onClick={() => { setOffen({ art: 'ablehnung', gruppe }) }}>
                Ablehnen
              </Knopf>
            </p>
          ) : (
            <p className="aktionen">
              {/* Die Knöpfe heißen wie der Dialog, den sie öffnen; die Knöpfe IM Dialog heißen nach
                  der Tat („Auszahlung buchen", „Stornorechnung ausstellen"). Zwei gleich benannte
                  Knöpfe auf einem Bildschirm, von denen einer im offenen Dialog steht, wären sonst
                  nicht auseinanderzuhalten. */}
              <Knopf onClick={() => { setOffen({ art: 'auszahlung', rechnung: alsRechnungsZeile(gruppe) }) }}>
                Auszahlung erfassen
              </Knopf>
              <Knopf
                variante="still"
                onClick={() => { setOffen({ art: 'storno', rechnung: alsRechnungsZeile(gruppe) }) }}
              >
                Rechnung stornieren
              </Knopf>
            </p>
          )}
        </section>
      ))}

      {offen?.art === 'freigabe' ? (
        <FreigabeDialog
          tenantId={tenantId}
          gruppe={offen.gruppe}
          jahr={jahr}
          onSchliessen={() => { setOffen(null) }}
          onFreigegeben={erledigt}
        />
      ) : null}
      {offen?.art === 'ablehnung' ? (
        <AblehnungDialog
          tenantId={tenantId}
          gruppe={offen.gruppe}
          onSchliessen={() => { setOffen(null) }}
          onErledigt={erledigt}
        />
      ) : null}
      {offen?.art === 'auszahlung' ? (
        <AuszahlungDialog
          tenantId={tenantId}
          rechnung={offen.rechnung}
          onSchliessen={() => { setOffen(null) }}
          onErledigt={erledigt}
        />
      ) : null}
      {offen?.art === 'storno' ? (
        <StornoDialog
          tenantId={tenantId}
          rechnung={offen.rechnung}
          onSchliessen={() => { setOffen(null) }}
          onErledigt={erledigt}
        />
      ) : null}
    </div>
  )
}

export interface FreigabeDialogProps {
  tenantId: string
  gruppe: RechnungsGruppe
  jahr: number
  onFreigegeben: (rechnung: RechnungsZeile) => void
  onSchliessen: () => void
}

export function FreigabeDialog({
  tenantId, gruppe, jahr, onFreigegeben, onSchliessen,
}: FreigabeDialogProps): ReactNode {
  const freigabe = useMutation({
    mutationFn: () => api.post<{ invoice: RechnungsZeile }>(
      `/api/t/${tenantId}/rechnungen/${gruppe.invoice_id}/approve`, {}),
    onSuccess: (antwort) => { onFreigegeben(antwort.invoice) },
  })

  return (
    <Dialog titel="Rechnung freigeben" offen onSchliessen={onSchliessen}>
      {/* Der Betrag steht im Dialog, nicht nur auf dem Bildschirm dahinter: freigegeben wird eine
          Zahl, und wer eine Zahl bestätigt, muss sie im selben Blickfeld haben wie den Knopf. Netto
          und Umsatzsteuer stehen daneben, weil zwischen ihnen der Unterschied zwischen einem
          Kleinunternehmer und einem regelbesteuerten Aussteller sichtbar wird. */}
      <p className="erklaerung">
        Aussteller: {gruppe.empfaenger_name}. Freigegeben wird ein Bruttobetrag von{' '}
        <strong><Betrag cents={gruppe.gross_cents} /></strong> (netto <Betrag cents={gruppe.net_cents} />,
        Umsatzsteuer <Betrag cents={gruppe.vat_cents} />) über {gruppe.positionen.length}{' '}
        {gruppe.positionen.length === 1 ? 'Position' : 'Positionen'}.
      </p>
      <p className="erklaerung">
        Die Rechnung erhält bei der Freigabe die nächste freie Nummer aus der Serie{' '}
        {serienName(gruppe.rechnungspraefix, jahr)}.
      </p>
      {/* Was danach ohne weiteres Zutun passiert, gehört vor den Klick und nicht in die
          Verfahrensdokumentation: der Ablauf schickt eine E-Mail an eine Person, und eine E-Mail
          holt niemand zurück (drain.ts, Dokumenten- und Mail-Bahn). */}
      <p className="erklaerung">
        Unmittelbar danach erzeugt das System die Rechnung als PDF und schickt sie dem Aussteller per
        E-Mail, mit einer Kopie an die Rechnungsadresse dieses Auftraggebers. Das PDF ist ab dann
        unveränderlich.
      </p>
      <p className="erklaerung">
        Die Freigabe lässt sich nicht rückgängig machen. Eine freigegebene Rechnung kann nur durch eine
        Stornorechnung aufgehoben werden, solange sie nicht ausgezahlt ist.
      </p>
      {/* Ob dies eine Selbstfreigabe ist, entscheidet der Server beim Freigeben und schreibt es in
          `self_approved`; die Oberfläche leitet es nicht her (Vertrag: sie entscheidet nichts, was
          der Server entscheidet). Der Satz unten sagt deshalb, was passiert — nicht, was gilt. */}
      <p className="erklaerung">
        Gehört dieses Auftragnehmer-Profil Ihnen selbst, vermerkt der Server die Freigabe als
        Selbstfreigabe auf der Rechnung und im Steuerberater-Export.
      </p>
      {gruppe.profil_geprueft ? null : (
        <p className="warnung">Das Steuerprofil dieses Ausstellers ist noch nicht geprüft.</p>
      )}
      {gruppe.rueckbuchung ? (
        <p className="warnung">Diese Abrechnung enthält eine Rückbuchung.</p>
      ) : null}
      {freigabe.isError ? <p role="alert" className="fehler">{fehlermeldung(freigabe.error)}</p> : null}
      {/* `erstfokus` liegt auf „Abbrechen“, nicht auf „Jetzt freigeben“: die Eingabetaste, die den
          Dialog geöffnet hat, wiederholt sich, solange sie gehalten wird, und die erste
          Wiederholung träfe sonst den Knopf, der die Rechnungsnummer vergibt. */}
      <p className="knoepfe">
        <Knopf disabled={freigabe.isPending} onClick={() => { freigabe.mutate() }}>Jetzt freigeben</Knopf>
        <Knopf variante="still" erstfokus onClick={onSchliessen}>Abbrechen</Knopf>
      </p>
    </Dialog>
  )
}

export interface AblehnungDialogProps {
  tenantId: string
  gruppe: RechnungsGruppe
  onErledigt: (rechnung: RechnungsZeile) => void
  onSchliessen: () => void
}

export function AblehnungDialog({
  tenantId, gruppe, onErledigt, onSchliessen,
}: AblehnungDialogProps): ReactNode {
  const [kommentar, setKommentar] = useState('')
  const [pruefFehler, setPruefFehler] = useState<Eingabefehler | null>(null)
  const ablehnung = useMutation({
    mutationFn: (comment: string) => api.post<{ invoice: RechnungsZeile }>(
      `/api/t/${tenantId}/rechnungen/${gruppe.invoice_id}/reject`, { comment }),
    onSuccess: (antwort) => { onErledigt(antwort.invoice) },
  })

  const absenden = (): void => {
    const geprueft = rejectSchema.safeParse({ comment: kommentar })
    if (!geprueft.success) {
      // Der Satz des letzten Serverfehlers gilt für einen Versuch, der gar nicht mehr hinausgeht;
      // er wird verworfen, damit nicht zwei Meldungen zugleich dastehen.
      ablehnung.reset()
      setPruefFehler(ersterFehler(geprueft.error))
      return
    }
    setPruefFehler(null)
    ablehnung.mutate(geprueft.data.comment)
  }
  return (
    <Dialog titel="Rechnung ablehnen" offen onSchliessen={onSchliessen}>
      <p className="erklaerung">
        Aussteller: {gruppe.empfaenger_name}. Die Positionen werden wieder offen und können erneut
        abgerechnet werden. Der Auftragnehmer erhält Ihre Begründung per E-Mail.
      </p>
      <Feld label="Begründung" htmlFor="rg-ablehnung" fehler={meldungFuer(pruefFehler, 'comment')}>
        <textarea
          id="rg-ablehnung"
          rows={3}
          value={kommentar}
          onChange={(e) => { setKommentar(e.target.value) }}
        />
      </Feld>
      {/* Der Satz des Servers steht neben dem Feld, nicht darin: er gehört dem Vorgang, nicht der
          Eingabe, und würde die Begründung sonst als ungültig markieren. */}
      {ablehnung.isError ? <p role="alert" className="fehler">{fehlermeldung(ablehnung.error)}</p> : null}
      <p className="knoepfe">
        <Knopf variante="warnung" disabled={ablehnung.isPending} onClick={absenden}>Ablehnen</Knopf>
        <Knopf variante="still" onClick={onSchliessen}>Abbrechen</Knopf>
      </p>
    </Dialog>
  )
}

export interface AuszahlungDialogProps {
  tenantId: string
  rechnung: RechnungsZeile
  onErledigt: (rechnung: RechnungsZeile) => void
  onSchliessen: () => void
}

export function AuszahlungDialog({
  tenantId, rechnung, onErledigt, onSchliessen,
}: AuszahlungDialogProps): ReactNode {
  const [wertstellung, setWertstellung] = useState('')
  const [referenz, setReferenz] = useState('')
  const [pruefFehler, setPruefFehler] = useState<Eingabefehler | null>(null)
  const auszahlung = useMutation({
    mutationFn: (koerper: { paid_at: string; amount_cents: number; bank_reference: string }) =>
      api.post<{ invoice: RechnungsZeile }>(`/api/t/${tenantId}/rechnungen/${rechnung.id}/payout`, koerper),
    onSuccess: (antwort) => { onErledigt(antwort.invoice) },
  })

  const absenden = (): void => {
    // Der Betrag wird nicht getippt: eine Rechnung wird in genau einer Überweisung bezahlt, und der
    // Bruttobetrag steht am Dokument. Er geht so hinaus, wie der Server ihn geliefert hat.
    const geprueft = payoutSchema.safeParse({
      paid_at: wertstellung, amount_cents: rechnung.gross_cents, bank_reference: referenz,
    })
    if (!geprueft.success) {
      auszahlung.reset()
      setPruefFehler(ersterFehler(geprueft.error))
      return
    }
    setPruefFehler(null)
    auszahlung.mutate(geprueft.data)
  }
  // Neben dem Feld steht, was der Benutzer dort ändern kann; im Satz des Vorgangs steht, was der
  // Server sagt — und eine Meldung zu `amount_cents`, das hier niemand tippt, hat kein Feld und
  // stünde sonst nirgends.
  const vorgangsFehler =
    pruefFehler !== null && pruefFehler.feld !== 'paid_at' && pruefFehler.feld !== 'bank_reference'
      ? pruefFehler.meldung
      : auszahlung.isError ? fehlermeldung(auszahlung.error) : null

  return (
    <Dialog titel="Auszahlung erfassen" offen onSchliessen={onSchliessen}>
      <p className="erklaerung">
        Rechnung {rechnung.number ?? rechnung.id}. Auszuzahlender Bruttobetrag:{' '}
        <Betrag cents={rechnung.gross_cents} />. Eine Rechnung wird in genau einer Überweisung
        bezahlt; Teilzahlungen gibt es nicht, der Betrag ist deshalb nicht änderbar.
      </p>
      <Feld
        label="Wertstellung der Überweisung"
        htmlFor="rg-wertstellung"
        fehler={meldungFuer(pruefFehler, 'paid_at')}
      >
        <input
          id="rg-wertstellung"
          type="date"
          value={wertstellung}
          onChange={(e) => { setWertstellung(e.target.value) }}
        />
      </Feld>
      <Feld
        label="Verwendungszweck oder Referenz"
        htmlFor="rg-referenz"
        fehler={meldungFuer(pruefFehler, 'bank_reference')}
      >
        <input
          id="rg-referenz"
          type="text"
          value={referenz}
          onChange={(e) => { setReferenz(e.target.value) }}
        />
      </Feld>
      <p className="kosten">
        Nach dem Buchen erzeugt das System den Auszahlungsbeleg und schickt ihn mit der Rechnung per
        E-Mail.
      </p>
      {vorgangsFehler === null ? null : <p role="alert" className="fehler">{vorgangsFehler}</p>}
      <p className="knoepfe">
        <Knopf disabled={auszahlung.isPending} onClick={absenden}>Auszahlung buchen</Knopf>
        <Knopf variante="still" onClick={onSchliessen}>Abbrechen</Knopf>
      </p>
    </Dialog>
  )
}

export interface StornoDialogProps {
  tenantId: string
  rechnung: RechnungsZeile
  /** Beide Zeilen der Antwort: die stornierte Rechnung und die Stornorechnung, die sie aufhebt. */
  onErledigt: (original: RechnungsZeile, storno: RechnungsZeile) => void
  onSchliessen: () => void
}

export function StornoDialog({
  tenantId, rechnung, onErledigt, onSchliessen,
}: StornoDialogProps): ReactNode {
  const [grund, setGrund] = useState('')
  const [pruefFehler, setPruefFehler] = useState<Eingabefehler | null>(null)
  const storno = useMutation({
    // Der Server gibt beide Zeilen zurück (services/billing.ts, `CancelResult`). Die Stornorechnung
    // trägt die Nummer, die der Text unten verspricht — sie wird deshalb mit weitergereicht.
    mutationFn: (reason: string) => api.post<{ original: RechnungsZeile; cancellation: RechnungsZeile }>(
      `/api/t/${tenantId}/rechnungen/${rechnung.id}/cancel`, { reason }),
    onSuccess: (antwort) => { onErledigt(antwort.original, antwort.cancellation) },
  })

  const absenden = (): void => {
    const geprueft = cancelSchema.safeParse({ reason: grund })
    if (!geprueft.success) {
      storno.reset()
      setPruefFehler(ersterFehler(geprueft.error))
      return
    }
    setPruefFehler(null)
    storno.mutate(geprueft.data.reason)
  }
  return (
    <Dialog titel="Rechnung stornieren" offen onSchliessen={onSchliessen}>
      <p className="erklaerung">
        Rechnung {rechnung.number ?? rechnung.id}. Es wird eine Stornorechnung mit eigener Nummer aus
        derselben Serie ausgestellt, als PDF erzeugt und per E-Mail versendet. Die ursprüngliche
        Rechnung gilt danach als storniert, ihre Positionen werden wieder offen und können neu
        abgerechnet werden.
      </p>
      <p className="erklaerung">Stornieren geht nur, solange die Rechnung nicht ausgezahlt ist.</p>
      <Feld label="Grund" htmlFor="rg-storno" fehler={meldungFuer(pruefFehler, 'reason')}>
        <textarea id="rg-storno" rows={3} value={grund} onChange={(e) => { setGrund(e.target.value) }} />
      </Feld>
      {storno.isError ? <p role="alert" className="fehler">{fehlermeldung(storno.error)}</p> : null}
      <p className="knoepfe">
        <Knopf variante="warnung" disabled={storno.isPending} onClick={absenden}>
          Stornorechnung ausstellen
        </Knopf>
        <Knopf variante="still" onClick={onSchliessen}>Abbrechen</Knopf>
      </p>
    </Dialog>
  )
}
